import React, { useState, useEffect } from 'react';
import { LoadingAnimatedInline } from '../components/Loading';
import { useAuth } from '../auth/AuthContext';
import { adminDownloadsGet, type AdminGetDownloads_Data } from './Downloads';

type NewUserSoftwarePermissions = {
  softwareKey: string;
  startDate: string;
  endDate: string;
};

type NewUser = {
  email: string;
  username: string;
  clientName: string;
  tempPassword: string;
  activated: boolean;
  groups: string[];
  softwarePermissions: NewUserSoftwarePermissions[];
};

const GROUP_OPTIONS = [
  'groupAdministrator',
  'groupAuthor',
  'groupEditor',
  'groupManager',
  'groupPublisher',
  'groupRegistered',
  'groupSuperUser'
];

const AddUsers: React.FC<{
  onUserAdded?: () => void;
}> = ({ onUserAdded }) => {
  const { user } = useAuth();
  const [downloads, setDownloads] = useState<AdminGetDownloads_Data[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<NewUser>({
    email: '',
    username: '',
    clientName: '',
    tempPassword: '',
    activated: true,
    groups: [],
    softwarePermissions: []
  });

  // Fetch downloads on component mount
  useEffect(() => {
    const fetchDownloads = async () => {
      let data: AdminGetDownloads_Data[] = [];
      try {
        data = await adminDownloadsGet();
      } catch (e) {
        console.error('Error fetching downloads:', e);
      }
      setDownloads(data);
      setLoading(false);
    };
    fetchDownloads();
  }, []);

  const handleFormChange = (field: keyof NewUser, value: any) => {
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleAddGroup = (group: string) => {
    if (!formData.groups.includes(group)) {
      setFormData({
        ...formData,
        groups: [...formData.groups, group]
      });
    }
  };

  const handleRemoveGroup = (group: string) => {
    setFormData({
      ...formData,
      groups: formData.groups.filter((g) => g !== group)
    });
  };

  const handleAddSoftwarePermission = (softwareKey: string) => {
    if (
      !formData.softwarePermissions.some(
        (perm) => perm.softwareKey === softwareKey
      )
    ) {
      setFormData({
        ...formData,
        softwarePermissions: [
          ...formData.softwarePermissions,
          {
            softwareKey,
            startDate: new Date().toISOString().split('T')[0],
            endDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split('T')[0]
          }
        ]
      });
    }
  };

  const handleRemoveSoftwarePermission = (softwareKey: string) => {
    setFormData({
      ...formData,
      softwarePermissions: formData.softwarePermissions.filter(
        (perm) => perm.softwareKey !== softwareKey
      )
    });
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.email || !formData.username) {
      setError('Email and Username are required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const res = await fetch('/api/admin/users/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || 'Failed to create user');
      }

      alert('User created successfully');
      setFormData({
        email: '',
        username: '',
        clientName: '',
        tempPassword: '',
        activated: true,
        groups: [],
        softwarePermissions: []
      });
      onUserAdded?.();
    } catch (e) {
      console.error('Error creating user:', e);
      setError(e instanceof Error ? e.message : 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <LoadingAnimatedInline />;

  return (
    <div className="p-6 h-full flex flex-col">
      <h2 className="text-2xl font-bold mb-6">Add New User</h2>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Form */}
      <div className="flex-1 overflow-y-auto border border-light-border rounded-md p-6">
        <div className="max-w-2xl space-y-6">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              placeholder="user@example.com"
              className="w-full border border-light-border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
            />
          </div>

          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Username <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => handleFormChange('username', e.target.value)}
              placeholder="Enter username"
              className="w-full border border-light-border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
            />
          </div>

          {/* Client Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client Name
            </label>
            <input
              type="text"
              value={formData.clientName}
              onChange={(e) => handleFormChange('clientName', e.target.value)}
              placeholder="Enter client name"
              className="w-full border border-light-border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
            />
          </div>
          {/* Temporary Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Temporary Password</label>
            <input
            className="w-full border border-light-border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
            placeholder="Enter temporary password"
            type="password"
            value={formData.tempPassword}
            onChange={(e) => handleFormChange('tempPassword', e.target.value)} />
          </div>


          {/* Activated */}
          <div>
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <input
                type="checkbox"
                checked={formData.activated}
                onChange={(e) =>
                  handleFormChange('activated', e.target.checked)
                }
                className="w-4 h-4 rounded border-light-border"
              />
              Account Activated
            </label>
          </div>

          {/* Groups */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Groups
            </label>
            <div className="space-y-3">
              {/* Selected Groups */}
              <div className="bg-gray-50 rounded-md p-4 min-h-12">
                <div className="text-xs text-gray-600 mb-2 font-semibold">
                  Selected:
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.groups.length > 0 ? (
                    formData.groups.map((group) => (
                      <div
                        key={group}
                        className="bg-light-main text-light-text-alt px-3 py-1 rounded text-sm flex items-center gap-2"
                      >
                        {group}
                        <button
                          onClick={() => handleRemoveGroup(group)}
                          className="ml-1 hover:opacity-70 transition-opacity"
                          type="button"
                        >
                          ✕
                        </button>
                      </div>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">
                      No groups selected
                    </span>
                  )}
                </div>
              </div>

              {/* Available Groups */}
              <div>
                <div className="text-xs text-gray-600 mb-2 font-semibold">
                  Available Groups:
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {GROUP_OPTIONS.map((group) => (
                    <button
                      key={group}
                      onClick={() => handleAddGroup(group)}
                      disabled={formData.groups.includes(group)}
                      className="text-left px-3 py-2 border border-light-border rounded hover:bg-light-main-hover hover:text-light-text-alt transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      type="button"
                    >
                      + {group}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Software Permissions Section */}
          <div className="pt-6 border-t border-light-border">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Software Permissions
            </h3>
            <div className="text-s text-gray-600 mb-2 font-semibold">
              Selected Software Permissions
            </div>
            <div className="space-y-3 mb-4">
              {formData.softwarePermissions.length > 0 ? (
                formData.softwarePermissions.map((perm) => (
                  <div
                    className="grid grid-cols-4 bg-gray-50 text-gray-700 text-sm rounded-md p-4"
                    key={perm.softwareKey}
                  >
                    <span>{perm.softwareKey}</span>
                    <div>
                      <label className="block text-xs text-gray-500">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={perm.startDate}
                        onChange={(event) => {
                          const newStartDate = (
                            event.target as HTMLInputElement
                          ).value;
                          setFormData({
                            ...formData,
                            softwarePermissions:
                              formData.softwarePermissions.map((p) =>
                                p.softwareKey === perm.softwareKey
                                  ? { ...p, startDate: newStartDate }
                                  : p
                              )
                          });
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={perm.endDate}
                        onChange={(event) => {
                          const newEndDate = (event.target as HTMLInputElement)
                            .value;
                          setFormData({
                            ...formData,
                            softwarePermissions:
                              formData.softwarePermissions.map((p) =>
                                p.softwareKey === perm.softwareKey
                                  ? { ...p, endDate: newEndDate }
                                  : p
                              )
                          });
                        }}
                      />
                    </div>
                    <button
                      onClick={() =>
                        handleRemoveSoftwarePermission(perm.softwareKey)
                      }
                      className="mr-2 flex items-center justify-end text-red-500 hover:text-red-700"
                    >
                      ✕
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-gray-400 text-sm">
                  No software permissions selected
                </div>
              )}
            </div>
            <div>
              <div className="text-xs text-gray-600 mb-2 font-semibold">
                Available Software Permissions
              </div>
              <div className="grid grid-cols-2 gap-2">
                {downloads.length > 0 ? (
                  downloads.map((download) => (
                    <button
                      key={download.key}
                      className="text-left px-3 py-2 border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-light-main-hover hover:text-light-text-alt border-light-border rounded text-sm bg-gray-50"
                      disabled={formData.softwarePermissions.some(
                        (perm) => perm.softwareKey === download.key
                      )}
                      type="button"
                      onClick={() => handleAddSoftwarePermission(download.key)}
                    >
                      + {download.displayName}
                    </button>
                  ))
                ) : (
                  <span className="text-gray-400 text-sm">
                    No software items available
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-2 pt-4">
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-6 py-2 bg-light-main text-white rounded-md hover:bg-light-main-hover transition-colors duration-200 ease-in-out font-medium disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create User'}
            </button>
            <button
              onClick={() => {
                setFormData({
                  email: '',
                  username: '',
                  clientName: '',
                  tempPassword: '',
                  activated: true,
                  groups: [],
                  softwarePermissions: []
                });
                setError(null);
              }}
              className="px-6 py-2 border border-light-border rounded-md hover:bg-gray-50 transition-colors duration-200 ease-in-out text-gray-700"
              type="button"
            >
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddUsers;
